import type { WikiPage } from '@/types/wiki'

export const CHANGELOG_CATEGORY_SLUG = 'changelog'

const MONTHS: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export interface PatchInfo {
  page: WikiPage
  /** "v0.5.0" parsed from the page title, or null when the title has no version. */
  version: string | null
  /** "Jul 26" parsed from the page title, falling back to the created_at date. */
  dateLabel: string
  /** "July 2026" group label. */
  monthLabel: string
  /** Milliseconds timestamp of the patch date (title date, year from created_at). */
  dateMs: number
}

export function isChangelogPage(page: WikiPage): boolean {
  return page.category_slug === CHANGELOG_CATEGORY_SLUG
}

/**
 * Changelog titles are authored as "v0.5.0 - Jul 26".
 * Parse them defensively: pages with unexpected titles still get a
 * timeline entry, dated by created_at.
 */
export function parsePatchInfo(page: WikiPage): PatchInfo {
  const title = page.title || ''
  const created = new Date(page.created_at)

  const versionMatch = title.match(/v\d+(?:\.\d+)*/i)
  const version = versionMatch ? versionMatch[0] : null

  const dateMatch = title.match(/([A-Za-z]{3,9})\s+(\d{1,2})\s*$/)
  let date = created
  if (dateMatch) {
    const month = MONTHS[dateMatch[1].slice(0, 3).toLowerCase()]
    const day = parseInt(dateMatch[2], 10)
    if (month !== undefined && day >= 1 && day <= 31) {
      // The title carries no year; the page row is created within days of the
      // patch, so created_at's year is correct (nudged when the title month
      // wraps around a year boundary, e.g. a January patch created in December).
      let year = created.getFullYear()
      const diff = month - created.getMonth()
      if (diff > 6) year -= 1
      else if (diff < -6) year += 1
      date = new Date(year, month, day)
    }
  }

  return {
    page,
    version,
    dateLabel: `${date.toLocaleDateString('en-US', { month: 'short' })} ${date.getDate()}`,
    monthLabel: `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`,
    dateMs: date.getTime(),
  }
}

/** Changelog pages as timeline entries, newest patch first. */
export function buildPatchTimeline(pages: WikiPage[]): PatchInfo[] {
  return pages
    .filter(isChangelogPage)
    .map(parsePatchInfo)
    .sort((a, b) => b.dateMs - a.dateMs || b.page.id - a.page.id)
}

/** Same slug rules as TableOfContents so hub links land on rendered anchors. */
export function slugifyHeading(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export interface PatchSection {
  id: string
  text: string
}

/**
 * Top-level sections (h2) of a patch page, with the anchor ids the rendered
 * page will assign. Used for the hub spotlight's jump chips.
 */
export function extractPatchSections(contentHtml: string): PatchSection[] {
  if (!contentHtml || typeof DOMParser === 'undefined') return []
  try {
    const doc = new DOMParser().parseFromString(contentHtml, 'text/html')
    const used = new Map<string, number>()
    const sections: PatchSection[] = []
    doc.querySelectorAll('h2, h3').forEach((el, idx) => {
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim()
      if (!text) return
      let base = slugifyHeading(text)
      if (!base) base = `heading-${idx}`
      const nextCount = (used.get(base) || 0) + 1
      used.set(base, nextCount)
      const id = nextCount === 1 ? base : `${base}-${nextCount}`
      if (el.tagName === 'H2') sections.push({ id, text })
    })
    return sections
  } catch {
    return []
  }
}
